import React, { useEffect, useRef } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { database } from "./firebaseConfig";
import { createSafeListener, cleanupListeners } from "./firebaseUtils";
import notificationSound from "../assets/images/notify.wav";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function NotificationListener() {
  const notificationAudioRef = useRef(null);
  const lastNotifiedRef = useRef({});
  const listenersRef = useRef([]);

  useEffect(() => {
    if ("Notification" in window && Notification.permission !== "granted") {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    const auth = getAuth();
    let unsubscribeAuth = null;
    let isComponentMounted = true;

    try {
      unsubscribeAuth = onAuthStateChanged(auth, (user) => {
        if (user && isComponentMounted) {
          try {
            // Create safe listener for user chats
            const chatListener = createSafeListener(
              database,
              `users/${user.uid}/chats`,
              (snapshot) => {
                if (!isComponentMounted) return;
                
                try {
                  const data = snapshot.val();
                  if (data) {
                    Object.entries(data).forEach(([userId, chatData]) => {
                      if (!isComponentMounted) return;
                      
                      const lastMsg = chatData.lastMessage;
                      if (
                        lastMsg &&
                        lastMsg.unread &&
                        lastMsg.user?._id !== user.uid &&
                        lastNotifiedRef.current[userId] !== lastMsg.createdAt
                      ) {
                        if (notificationAudioRef.current) {
                          notificationAudioRef.current.currentTime = 0;
                          notificationAudioRef.current.play().catch(console.error);
                        }
                        if ("Notification" in window && Notification.permission === "granted") {
                          new Notification("New Message", {
                            body: `${chatData.user?.name}: ${lastMsg.text ? lastMsg.text : "Sent an attachment"}`,
                            icon: chatData.user?.profile_image || undefined,
                          });
                        }
                        toast.info(
                          <div>
                            <strong>{chatData.user?.name}</strong>: {lastMsg.text ? lastMsg.text : "Sent an attachment"}
                          </div>,
                          {
                            position: "top-right",
                            autoClose: 5000,
                            hideProgressBar: false,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                          }
                        );
                        lastNotifiedRef.current[userId] = lastMsg.createdAt;
                      }
                    });
                  }
                } catch (error) {
                  console.error("Error processing notification data:", error);
                }
              },
              (error) => {
                console.error("Firebase database error:", error);
              }
            );

            // Store listener for cleanup
            listenersRef.current.push(chatListener);
          } catch (error) {
            console.error("Error setting up Firebase listener:", error);
          }
        }
      });
    } catch (error) {
      console.error("Error setting up auth listener:", error);
    }

    return () => {
      isComponentMounted = false;
      
      try {
        // Cleanup all Firebase listeners
        cleanupListeners(listenersRef.current);
        listenersRef.current = [];
        
        // Cleanup auth listener
        if (unsubscribeAuth && typeof unsubscribeAuth === 'function') {
          unsubscribeAuth();
        }
      } catch (error) {
        console.warn("Error during cleanup:", error);
      }
    };
  }, []);

  return (
    <>
      <audio ref={notificationAudioRef} src={notificationSound} preload="auto" />
      {/* <ToastContainer/> */}
    </>
  );
}